/**
 * «المخزون» — every word on the stock page (tasks/stock.md step 4).
 *
 * ⚠️ ARABIC-ONLY, deliberately — like every ops page (config/unitStatusLabels.js, guestReviewLabels.js).
 * Only ops use this page (O5); item names carry their own English line beside the Arabic.
 *
 * The backend answers in CODES (units, entry kinds, STOCK_xxx refusals); this file is the one place
 * they become words.
 */
import { STOCK_ERROR_CODES as C } from '../utils/stockErrorCodes';
import { formatCount } from '../utils/numberFormatter';

/** «صنف واحد» / «صنفان» / «٣ أصناف» / «١١ صنفاً» — Arabic number agreement. */
export const itemCount = (n) => {
  if (n === 1) return 'صنف واحد';
  if (n === 2) return 'صنفان';
  return `${formatCount(n)} ${n >= 3 && n <= 10 ? 'أصناف' : 'صنفاً'}`;
};

export const TEXT = {
  pageTitle: 'المخزون',
  pageSubtitle: 'مستلزمات الضيافة في المخزن: ما على الرف، ومن استلم، ومتى نشتري.',
  refresh: 'تحديث',
  loading: 'جارٍ التحميل…',
  loadError: 'تعذّر تحميل المخزون.',
  unexpected: 'حدث خطأ غير متوقع. حاول مرة أخرى.',

  // First run
  noStoreTitle: 'ابدأ بإنشاء المخزن',
  noStoreHint: 'المخزن هو المكان الذي تُحفظ فيه المستلزمات. بعدها تضيف الأصناف بالعدد الموجود على الرف.',
  storeNameLabel: 'اسم المخزن',
  storeNameDefault: 'المخزن الرئيسي',
  createStore: 'إنشاء المخزن',
  emptyTitle: 'المخزن فارغ',
  emptyHint: 'أضف أول صنف بالعدد الموجود على الرف الآن.',

  // Actions
  addItem: 'إضافة صنف',
  showArchived: 'عرض المؤرشفة',

  // Tabs
  tabItems: 'الأصناف',
  tabPeople: 'حسب الموظف',

  // Counts and banners
  countItems: 'صنف',
  countLow: 'منخفض',
  countNegative: 'عدده غير صحيح',
  countOverdue: 'يحتاج جرد',
  overdueBanner: (n, days) => `${itemCount(n)} لم ${n === 1 ? 'يُجرد' : 'تُجرد'} منذ أكثر من ${formatCount(days)} أيام.`,
  negativeBanner: (n) => `${itemCount(n)} ${n === 1 ? 'رصيده' : 'رصيدها'} أقل من صفر — لا يطابق الرف. اجرد لتصحيحه.`,
  startRecount: 'ابدأ الجرد',

  // Item tile
  stateOk: 'متوفر',
  stateLow: 'منخفض',
  stateNegative: 'العدد غير صحيح',
  stateArchived: 'مؤرشف',
  lastCounted: 'آخر جرد',
  neverCounted: 'لم يُجرد بعد',
  overdue: 'تأخر الجرد',
  lowLevel: 'حد التنبيه',
  noPhoto: 'بلا صورة',

  // Item window
  itemCurrent: 'الرصيد الحالي',
  editItem: 'تعديل',
  save: 'حفظ',
  cancel: 'إلغاء',
  close: 'إغلاق',
  done: 'تم',
  nameAr: 'الاسم بالعربية',
  nameEn: 'الاسم بالإنجليزية',
  unit: 'وحدة العد',
  unitLocked: 'وحدة العد ثابتة بعد الإضافة — لتغييرها أرشف الصنف وأضفه من جديد.',
  lowStockLevel: 'حد التنبيه',
  lowStockLevelHint: 'يظهر الصنف «منخفض» عندما يصل الرصيد إلى هذا العدد أو أقل. اتركه فارغاً إن لم تحدده.',
  openingQuantity: 'العدد على الرف الآن',
  openingQuantityHint: 'يُسجَّل كعدّ افتتاحي للصنف.',
  photo: 'صورة الصنف',
  photoHint: 'صورة واضحة للمنتج تساعد على أخذ الصنف الصحيح. JPEG أو PNG أو WebP حتى 10 ميغابايت.',
  choosePhoto: 'اختيار صورة',
  changePhoto: 'تغيير الصورة',
  uploadingPhoto: 'جارٍ رفع الصورة…',
  photoFailedAfterCreate: 'أُضيف الصنف، لكن تعذّر رفع الصورة. أضفها من نافذة الصنف.',
  archive: 'أرشفة الصنف',
  restore: 'إعادة من الأرشيف',
  archiveHint: 'يختفي الصنف من القائمة ويبقى سجله كاملاً.',
  history: 'السجل',
  historyEmpty: 'لا توجد قيود بعد.',
  balanceAfter: 'الرصيد بعدها',
  takenBy: 'استلمه',
  recordedBy: 'سجّله',
  counted: 'العدد على الرف',
  difference: 'الفرق',
  reverse: 'تراجع',
  reversed: 'تم التراجع عنه',
  reverseTitle: 'التراجع عن هذا القيد',
  reverseHint: 'يُسجَّل قيد عكسي ويبقى القيد الأصلي في السجل.',
  reverseNote: 'سبب التراجع (اختياري)',
  reverseConfirm: 'تأكيد التراجع',
  reverseCountUnchanged: 'أُلغي القيد من سجل الموظف. العدد لم يتغير لأن جرداً لاحقاً صحّحه مسبقاً.',
  showingLatest: (shown, total) => `يظهر آخر ${formatCount(shown)} من ${formatCount(total)} قيد.`,

  // Entry sheet
  note: 'ملاحظة (اختياري)',
  taker: 'الموظف المستلم',
  takerPlaceholder: 'اختر الموظف…',
  takerGroupCrew: 'فريق الميدان',
  takerGroupOps: 'العمليات',
  invited: 'لم يفعّل حسابه',
  onShelf: 'الرصيد',
  linesChosen: (n) => itemCount(n),
  nothingEntered: 'أدخل كمية لصنف واحد على الأقل.',
  wholeNumber: 'اكتب عدداً صحيحاً.',
  atLeastOne: 'الكمية تبدأ من ١.',
  savedTitle: 'تم الحفظ',
  recountResult: 'نتيجة الجرد',
  recountMatch: 'مطابق',
  recountFound: 'العدد',
  replayedNote: 'هذا الطلب حُفظ من قبل — لم يُسجَّل مرتين.',

  // People tab
  peopleIntro: 'للمراجعة فقط: ما استلمه كل شخص من المخزن مقابل التنظيفات المسجلة له في الفترة. التنظيف المشترك يُحسب للشخصين، فاقرأ الأرقام لكل ثنائي.',
  person: 'الموظف',
  cleans: 'التنظيفات',
  peopleEmpty: 'لا توجد عمليات تسليم أو تنظيفات في هذه الفترة.',
  unnamed: 'بدون اسم',
};

/** The unit codes from `GET /api/v1/stock/options`. */
export const UNIT_LABELS = {
  piece: 'حبة',
  bottle: 'قارورة',
  pack: 'حزمة',
  box: 'علبة',
  carton: 'كرتون',
  roll: 'لفة',
  bag: 'كيس',
  gallon: 'جالون',
};

/** History entry kinds (StockMovement.kind). */
export const KIND_LABELS = {
  opening: 'عدّ افتتاحي',
  purchase: 'استلام مشتريات',
  take_out: 'تسليم',
  recount: 'جرد',
  reversal: 'تراجع عن قيد',
};

/** Period presets of the per-person check — the server resolves them in Riyadh. */
export const PERIOD_LABELS = {
  thisMonth: 'هذا الشهر',
  last7: 'آخر ٧ أيام',
  lastMonth: 'الشهر الماضي',
};

/** The three ways stock moves, as the page offers them. `kind` matches the API segment. */
export const ENTRY_ACTIONS = {
  purchase: {
    title: 'استلام مشتريات',
    hint: 'أدخل الكميات التي وصلت إلى المخزن.',
    save: 'حفظ الاستلام',
    saved: 'تم حفظ الاستلام.',
    icon: 'bi-box-arrow-in-down',
  },
  takeOut: {
    title: 'تسليم للفريق',
    hint: 'اختر الموظف، ثم أدخل ما استلمه من المخزن.',
    save: 'حفظ التسليم',
    saved: 'تم حفظ التسليم.',
    icon: 'bi-box-arrow-up',
  },
  recount: {
    title: 'جرد المخزن',
    hint: 'اعدد ما على الرف واكتب العدد الفعلي. اترك الصنف فارغاً إن لم تعدّه.',
    save: 'حفظ الجرد',
    saved: 'تم حفظ الجرد.',
    icon: 'bi-clipboard-check',
  },
};

/** The crew first in the handover picker — they are who stock is handed to. */
export const CREW_ROLES = ['maintenance', 'maintenance-supervisor'];

// Server refusal codes → words. Never read meaning off the server's Arabic `error` text (I18N_STRATEGY).
const ERROR_TEXT = {
  [C.STORE_NOT_FOUND]: 'المخزن غير موجود — حدّث الصفحة.',
  [C.ITEM_NOT_FOUND]: 'الصنف غير موجود — حدّث الصفحة.',
  [C.ENTRY_NOT_FOUND]: 'القيد غير موجود — حدّث الصفحة.',
  [C.NAME_REQUIRED]: 'اكتب الاسم بالعربية والإنجليزية.',
  [C.NAME_TOO_LONG]: 'الاسم طويل — الحد ٨٠ حرفاً.',
  [C.NAME_TAKEN]: 'الاسم مستخدم من قبل — اختر اسماً آخر.',
  [C.NOTE_INVALID]: 'الملاحظة طويلة — الحد ٥٠٠ حرف.',
  [C.UNIT_INVALID]: 'اختر وحدة العد.',
  [C.UNIT_LOCKED]: 'لا يمكن تغيير وحدة العد — أرشف الصنف وأضفه من جديد.',
  [C.LEVEL_INVALID]: 'حد التنبيه يجب أن يكون عدداً صحيحاً، صفراً أو أكثر.',
  [C.QUANTITY_INVALID]: 'اكتب عدداً صحيحاً.',
  [C.LINES_EMPTY]: 'أدخل كمية لصنف واحد على الأقل.',
  [C.LINES_DUPLICATE]: 'الصنف مكرر في النموذج.',
  [C.LINES_INVALID]: 'بيانات النموذج غير صالحة — أغلقه وافتحه من جديد.',
  [C.ITEM_ARCHIVED]: 'هذا الصنف مؤرشف — أعده من الأرشيف أولاً.',
  [C.NOT_REVERSIBLE]: 'لا يمكن التراجع عن الجرد — صحّح العدد بجرد جديد.',
  [C.TAKER_REQUIRED]: 'اختر الموظف المستلم.',
  [C.TAKER_INVALID]: 'الموظف المستلم غير متاح — اختر شخصاً آخر.',
  [C.PERIOD_INVALID]: 'الفترة غير صالحة.',
  [C.KEY_REQUIRED]: 'تعذّر الحفظ — أغلق النموذج وافتحه من جديد.',
  [C.KEY_CONFLICT]: 'تعذّر الحفظ — أغلق النموذج وافتحه من جديد.',
  [C.PHOTO_REQUIRED]: 'اختر صورة.',
  [C.PHOTO_TOO_LARGE]: 'الصورة كبيرة جداً — الحد ١٠ ميغابايت.',
  [C.PHOTO_BAD_TYPE]: 'نوع الصورة غير مدعوم — JPEG أو PNG أو WebP.',
};

/** A refused action, in words: the code when we know it, else the server's Arabic, else a generic line. */
export const errorText = (err) => {
  const data = err?.response?.data;
  if (data?.code && ERROR_TEXT[data.code]) return ERROR_TEXT[data.code];
  return data?.error || TEXT.unexpected;
};

/** An item's name for this page: Arabic first, English beneath. */
export const itemNames = (item) => ({ primary: item?.nameAr || '', secondary: item?.nameEn || '' });
